/**
 * Media Registry System
 * 
 * Provides media type renderers and formatters for different data types.
 * Each media type defines how data should be formatted and which UI components to use.
 */
import { z } from 'zod';
import {
  MediaType, FormatByMedia
} from '../../../core/template.schemas';

/**
 * Available field component tokens for form rendering
 */
export type FieldToken =
  | 'TextInput' | 'TextArea'
  | 'NumberInput'
  | 'DatePicker'
  | 'ColorPicker'
  | 'ImagePicker'
  | 'EntitySelect/Player' | 'EntitySelect/Team' | 'EntitySelect/Match' | 'EntitySelect/Venue'
  | 'AttributeSelectDefault';

/**
 * Media renderer configuration for a specific media type
 */
export type MediaRenderer<T = any> = {
  /** Zod schema for format validation */
  formatSchema: z.ZodType<FormatByMedia[MediaType]>;
  /** Function to apply formatting to raw data */
  applyFormat: (raw: any, fmt?: any) => T;
  /** Field component for editor mode */
  editorField: FieldToken;
  /** Field component for generator mode */
  generatorField: FieldToken;
  /** Optional validation function */
  validate?: (value: any) => boolean;
  /** Optional transformation function for input */
  transform?: (input: any) => any;
  /** Icon for UI display */
  icon?: string;
  /** Color theme for UI */
  color?: string;
  /** Display name for UI */
  displayName?: string;
};

/**
 * Registry for managing media type renderers and formatters
 */
export class MediaRegistry {
  /** Internal storage for media renderers */
  private renderers = new Map<MediaType, MediaRenderer>();

  /**
   * Register a media renderer
   * @param mediaType - Media type identifier
   * @param renderer - Media renderer configuration
   * @throws Error if media type is already registered
   */
  public register(mediaType: MediaType, renderer: MediaRenderer): void {
    if (this.renderers.has(mediaType)) {
      throw new Error(`Media type '${mediaType}' is already registered`);
    }
    this.renderers.set(mediaType, renderer);
  }

  /**
   * Get media renderer by type
   * @param mediaType - Media type identifier
   * @returns Media renderer or undefined if not found
   */
  public get(mediaType: MediaType): MediaRenderer | undefined {
    return this.renderers.get(mediaType);
  }

  /**
   * Check if media type is registered
   * @param mediaType - Media type identifier
   * @returns True if media type is registered
   */
  public has(mediaType: MediaType): boolean {
    return this.renderers.has(mediaType);
  }

  /**
   * Apply formatting to a value
   * @param mediaType - Media type identifier
   * @param raw - Raw value to format
   * @param format - Optional format configuration
   * @returns Formatted value
   */
  public applyFormat(mediaType: MediaType, raw: any, format?: any): any {
    const renderer = this.get(mediaType);
    if (!renderer) {
      throw new Error(`No renderer found for media type '${mediaType}'`);
    }
    return renderer.applyFormat(raw, format);
  }

  /**
   * Get field component for a media type
   * @param mediaType - Media type identifier
   * @param mode - Editor or generator mode
   * @returns Field component token
   */
  public getFieldComponent(mediaType: MediaType, mode: 'editor' | 'generator' = 'editor'): FieldToken {
    const renderer = this.get(mediaType);
    if (!renderer) {
      throw new Error(`No renderer found for media type '${mediaType}'`);
    }
    return mode === 'editor' ? renderer.editorField : renderer.generatorField;
  }

  /**
   * Validate a value for a media type
   * @param mediaType - Media type identifier
   * @param value - Value to validate
   * @returns True if valid, false otherwise
   */
  public validate(mediaType: MediaType, value: any): boolean {
    const renderer = this.get(mediaType);
    if (!renderer) {
      return false;
    }
    return renderer.validate ? renderer.validate(value) : true;
  }

  /**
   * Get all registered media types
   * @returns Array of registered media type identifiers
   */
  public getTypes(): MediaType[] {
    return Array.from(this.renderers.keys());
  }

  /**
   * Get all registered media renderers
   * @returns Array of all media renderers
   */
  public getAll(): Array<{ type: MediaType; renderer: MediaRenderer }> {
    return Array.from(this.renderers.entries()).map(([type, renderer]) => ({ type, renderer }));
  }

  /**
   * Unregister a media type
   * @param mediaType - Media type identifier
   * @returns True if media type was unregistered, false if not found
   */
  public unregister(mediaType: MediaType): boolean {
    return this.renderers.delete(mediaType);
  }

  /**
   * Clear all registered media types
   */
  public clear(): void {
    this.renderers.clear();
  }

  /**
   * Get count of registered media types
   */
  public size(): number {
    return this.renderers.size;
  }

  /**
   * Get format options for a media type
   * @param mediaType - Media type identifier
   * @returns Array of format option objects with type and label
   */
  public getFormatOptions(mediaType: MediaType): Array<{ type: string; label: string }> {
    const renderer = this.get(mediaType);
    if (!renderer) {
      return [];
    }

    // Return format options based on media type
    switch (mediaType) {
      case 'text':
        return [
          { type: 'default', label: 'Standard (unverändert)' },
          { type: 'uppercase', label: 'UPPERCASE' },
          { type: 'lowercase', label: 'lowercase' },
          { type: 'title', label: 'Title Case' }
        ];
      case 'int':
        return [
          { type: 'default', label: 'Standard (unverändert)' },
          { type: 'thousands', label: 'Mit Tausendertrennzeichen' },
        ];
      case 'float':
        return [
          { type: 'none', label: 'Keine Formatierung (Originalwert)' },
          { type: 'stripZeros', label: 'Standard (0-Enden entfernen)' },
          { type: 'decimal2', label: 'Standard (2 Dezimalstellen)' },
          { type: 'thousands_trim', label: 'Tausenderpunkt (Nachkommastellen nur wenn nötig)' },
          { type: 'thousands_2', label: 'Tausenderpunkt + 2 Dezimalstellen' },
          { type: 'integer', label: 'Ganzzahl (Dezimalen entfernen)' }
        ];
      case 'date':
        return [
          { type: 'DD.MM.YYYY', label: 'Standard (DD.MM.YYYY)' },
          { type: 'EEEE', label: 'Wochentag (Montag)' },
          { type: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
          { type: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
          { type: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
          { type: 'DD.MM.YYYY HH:mm Uhr', label: 'DD.MM.YYYY HH:mm Uhr' },
          { type: 'HH:mm Uhr', label: 'HH:mm Uhr' },
          { type: 'DD. MMMM', label: 'DD. MMMM' },
          { type: 'DD. MMMM YYYY', label: 'DD. MMMM YYYY' }
        ];
      case 'image':
        return [
          { type: 'contain', label: 'Standard (Contain)' },
          { type: 'cover', label: 'Cover' },
          { type: 'fill', label: 'Füllen' },
          { type: 'none', label: 'Keine' }
        ];
      case 'color':
        return [
          { type: 'default', label: 'Standard (Hex)' }
        ];
      default:
        return [];
    }
  }

  /**
   * Get display name for a media type
   * @param mediaType - Media type identifier
   * @returns Human-readable display name from registered renderer or fallback
   */
  public getDisplayName(mediaType: MediaType): string {
    const renderer = this.get(mediaType);
    if (renderer?.displayName) {
      return renderer.displayName;
    }
    
    // Fallback to media type identifier if no renderer or displayName found
    return mediaType;
  }
}

/**
 * Utility functions for number formatting
 */
export class NumberFormatter {
  /**
   * Formats numeric values with optional decimal places, thousands separators, and padding
   */
  static format(raw: any, options?: {
    decimals?: number;
    thousands?: boolean;
    pad?: number;
    trimZeros?: boolean;
    roundMode?: 'round' | 'floor' | 'ceil';
    preserve?: boolean;
  }): string {
    const n = Number(raw ?? 0);
    const thousands = options?.thousands ?? false;
    const locale = 'de-DE';

    if (options?.preserve) {
      const asString = String(raw ?? '');
      const normalized = asString.replace(/\./g, ',');
      if (!thousands) return normalized;
      const [intPart, fracPart] = normalized.split(',');
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return fracPart !== undefined ? `${grouped},${fracPart}` : grouped;
    }

    const decimals = options?.decimals ?? 0;

    const applyRound = (value: number) => {
      switch (options?.roundMode) {
        case 'floor':
          return Math.floor(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
        case 'ceil':
          return Math.ceil(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
        default:
          return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
      }
    };

    const rounded = applyRound(n);
    const nf = new Intl.NumberFormat(locale, {
      minimumFractionDigits: options?.trimZeros ? 0 : decimals,
      maximumFractionDigits: decimals,
      useGrouping: thousands,
    });

    let result = nf.format(rounded);

    if (options?.pad && options.pad > result.length) {
      result = result.padStart(options.pad, '0');
    }

    return result;
  }
}

/**
 * Utility functions for date formatting
 */
export class DateFormatter {
  /**
   * Formats date values with optional pattern
   */
  static format(raw: any, pattern?: string): string {
    if (!raw) return '';
    
    const date = new Date(raw);
    if (isNaN(date.getTime())) return String(raw);
    
    if (!pattern) {
      return date.toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" });
    }

    const monthNames = [
      "Januar", "Februar", "März", "April", "Mai", "Juni",
      "Juli", "August", "September", "Oktober", "November", "Dezember"
    ];

    // Extract parts in Berlin timezone
    const parts = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      weekday: 'long',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(date);

    const get = (t: string) => parts.find(p => p.type === t)?.value || '00';
    
    const yearStr = get('year');
    const monthStr = get('month');
    const dayStr = get('day');
    const weekdayStr = get('weekday');
    const hourStr = get('hour'); // 00-23
    const minuteStr = get('minute');
    
    const monthIndex = parseInt(monthStr, 10) - 1;

    // Simple pattern replacement - could be extended with a proper date formatting library
    return pattern
      .replace(/YYYY/g, yearStr)
      .replace(/EEEE/g, weekdayStr)
      .replace(/MMMM/g, monthNames[monthIndex] || '')
      .replace(/MM/g, monthStr)
      .replace(/DD/g, dayStr)
      .replace(/HH/g, hourStr)
      .replace(/mm/g, minuteStr);
  }
}
